<template>
    <div
        class="admin-global-search"
        v-click-outside="closePanel"
    >
        <div class="admin-global-search__field" role="search">
            <span class="admin-global-search__command-dot" aria-hidden="true"></span>
            <admin-icon name="search" class="admin-global-search__icon" aria-hidden="true"></admin-icon>
            <input
                ref="input"
                v-model="query"
                type="search"
                class="admin-global-search__input"
                :placeholder="trans('general.search_placeholder')"
                autocomplete="off"
                autocorrect="off"
                spellcheck="false"
                role="combobox"
                :aria-label="trans('general.search_placeholder')"
                aria-autocomplete="list"
                aria-haspopup="listbox"
                :aria-controls="listboxId"
                :aria-activedescendant="panelOpen && activeOptionId ? activeOptionId : false"
                :aria-expanded="panelOpen ? 'true' : 'false'"
                @focus="onFocus"
                @keydown.esc.prevent="closePanel"
                @keydown.down.prevent="moveActive(1)"
                @keydown.up.prevent="moveActive(-1)"
                @keydown.enter.prevent="goActive"
            />
            <kbd class="admin-global-search__kbd d-none d-md-inline-flex" aria-hidden="true">/</kbd>
        </div>

        <div
            v-show="panelOpen"
            :id="listboxId"
            class="admin-global-search__panel admin-pro-dropdown-panel"
            role="listbox"
        >
            <div v-if="loading" class="admin-global-search__state">{{ trans('general.searching') }}</div>
            <template v-else>
                <div v-if="queryTrim.length > 0 && queryTrim.length < 2" class="admin-global-search__state">
                    {{ trans('general.search_min_chars') }}
                </div>
                <template v-else-if="queryTrim.length >= 2">
                    <div v-if="navHits.length" class="admin-global-search__section">
                        <div class="admin-global-search__section-label">{{ trans('general.search_section_nav') }}</div>
                        <ul class="admin-global-search__list">
                            <li v-for="(item, idx) in navHits" :key="'n-'+item.path" role="presentation">
                                <router-link
                                    :id="optionId(idx)"
                                    :to="item.path"
                                    class="admin-global-search__item"
                                    :class="{ 'is-active': flatIndex === idx }"
                                    role="option"
                                    :aria-selected="flatIndex === idx ? 'true' : 'false'"
                                    @click.native="onPick"
                                >
                                    <span class="admin-global-search__item-icon" aria-hidden="true"><admin-icon name="grid"></admin-icon></span>
                                    <span class="admin-global-search__item-body">
                                        <span class="admin-global-search__item-title">{{ item.label }}</span>
                                        <span class="admin-global-search__item-meta">{{ item.path }}</span>
                                    </span>
                                </router-link>
                            </li>
                        </ul>
                    </div>

                    <div v-if="entityBlocks.length" class="admin-global-search__section">
                        <div class="admin-global-search__section-label">{{ trans('general.search_section_records') }}</div>
                        <template v-for="block in entityBlocks">
                            <div :key="block.type" class="admin-global-search__entity">
                                <div class="admin-global-search__entity-type">{{ entityTitle(block.type) }}</div>
                                <ul class="admin-global-search__list">
                                    <li v-for="(it, j) in block.items" :key="block.type + '-' + j + '-' + it.path" role="presentation">
                                        <router-link
                                            :id="optionId(navHits.length + flatEntityIndex(block, j))"
                                            :to="it.path"
                                            class="admin-global-search__item"
                                            :class="{ 'is-active': flatIndex === navHits.length + flatEntityIndex(block, j) }"
                                            role="option"
                                            :aria-selected="flatIndex === navHits.length + flatEntityIndex(block, j) ? 'true' : 'false'"
                                            @click.native="onPick"
                                        >
                                            <span class="admin-global-search__item-icon" aria-hidden="true"><admin-icon name="chevron"></admin-icon></span>
                                            <span class="admin-global-search__item-body">
                                                <span class="admin-global-search__item-title">{{ it.title }}</span>
                                                <span v-if="it.subtitle" class="admin-global-search__item-meta">{{ it.subtitle }}</span>
                                            </span>
                                        </router-link>
                                    </li>
                                </ul>
                            </div>
                        </template>
                    </div>

                    <div v-if="!navHits.length && !entityBlocks.length && !loading" class="admin-global-search__state">
                        {{ trans('general.search_no_results') }}
                    </div>
                </template>
                <div v-else class="admin-global-search__hint">{{ trans('general.search_hint_shortcut') }}</div>
            </template>
        </div>
    </div>
</template>

<script>
const NAV_CATALOG = [
    { path: '/', perm: null, i18n: 'general.home', kw: 'home dashboard main start' },
    { path: '/user', perm: 'list-user', i18n: 'user.user', kw: 'user users people staff accounts' },
    { path: '/instance', perm: 'list-instance', i18n: 'instance.instance', kw: 'instance instances tenant site domain database' },
    { path: '/subscription', perm: 'list-subscription', i18n: 'subscription.subscription', kw: 'subscription billing invoice renew' },
    { path: '/subscriber', perm: 'list-subscriber', i18n: 'subscriber.subscriber', kw: 'subscriber subscribers customer' },
    { path: '/query', perm: 'list-query', i18n: 'query.query', kw: 'query support ticket contact message' },
    { path: '/lead', perm: 'list-lead', i18n: 'lead.lead', kw: 'lead leads demo contact sales pipeline' },
    { path: '/blog', perm: 'list-blog', i18n: 'blog.blog', kw: 'blog post article content' },
    { path: '/plan', perm: 'list-plan', i18n: 'plan.plan', kw: 'plan plans pricing package tier' },
    { path: '/currency', perm: 'list-currency', i18n: 'currency.currency', kw: 'currency money price symbol' },
    { path: '/configuration', perm: 'access-configuration', i18n: 'configuration.configuration', kw: 'configuration settings setup options' },
    { path: '/configuration/basic', perm: 'access-configuration', i18n: 'general.basic', kw: 'basic company profile info' },
    { path: '/configuration/logo', perm: 'access-configuration', i18n: 'general.logo', kw: 'logo brand image' },
    { path: '/configuration/system', perm: 'access-configuration', i18n: 'general.system', kw: 'system timezone date format' },
    { path: '/configuration/mail', perm: 'access-configuration', i18n: 'mail.mail', kw: 'mail smtp email server' },
    { path: '/configuration/locale', perm: 'access-configuration', i18n: 'locale.locale', kw: 'locale language translation i18n' },
    { path: '/configuration/role', perm: 'access-configuration', i18n: 'role.role', kw: 'role roles' },
    { path: '/configuration/permission', perm: 'access-configuration', i18n: 'permission.permission', kw: 'permission access acl' },
    { path: '/configuration/authentication', perm: 'access-configuration', i18n: 'auth.authentication', kw: 'authentication login security 2fa' },
    { path: '/configuration/ip-filter', perm: 'access-configuration', i18n: 'ip_filter.ip_filter', kw: 'ip filter whitelist firewall' },
    { path: '/configuration/instance', perm: 'access-configuration', i18n: 'instance.instance', kw: 'instance defaults new site wizard' },
    { path: '/configuration/scheduled-job', perm: 'access-configuration', i18n: 'general.scheduled_job', kw: 'cron schedule job queue' },
    { path: '/configuration/payment', perm: 'access-configuration', i18n: 'configuration.payment_configuration', kw: 'payment gateway stripe paypal' },
    { path: '/configuration/server', perm: 'access-configuration', i18n: 'configuration.server_configuration', kw: 'server environment php' },
    { path: '/configuration/frontend', perm: 'access-configuration', i18n: 'configuration.frontend_configuration', kw: 'frontend theme ui website' },
    { path: '/backup', perm: 'access-configuration', i18n: 'backup.backup', kw: 'backup restore database dump' },
    { path: '/email-template', perm: 'access-configuration', i18n: 'template.email_template', kw: 'email template message' },
    { path: '/email-log', perm: 'access-configuration', i18n: 'mail.email_log', kw: 'email log history sent' },
    { path: '/activity-log', perm: 'access-configuration', i18n: 'activity.activity_log', kw: 'activity audit log history' },
    { path: '/todo', perm: 'access-todo', i18n: 'todo.todo', kw: 'todo task reminder' },
    { path: '/message', perm: 'access-message', i18n: 'message.message', kw: 'message inbox mail compose' },
    { path: '/profile', perm: null, i18n: 'user.profile', kw: 'profile account me avatar' },
    { path: '/change-password', perm: null, i18n: 'user.change_password', kw: 'password change security' },
    { path: '/about', perm: null, i18n: 'general.about', kw: 'about version info' },
    { path: '/support', perm: null, i18n: 'general.support', kw: 'support help contact' },
];

function normalize(s) {
    return (s || '').toLowerCase();
}

export default {
    directives: {
        clickOutside: {
            bind(el, binding, vnode) {
                el._clickOutside = function (e) {
                    if (!(el === e.target || el.contains(e.target))) {
                        binding.value(e);
                    }
                };
                document.addEventListener('click', el._clickOutside, true);
            },
            unbind(el) {
                document.removeEventListener('click', el._clickOutside, true);
            },
        },
    },
    data() {
        return {
            query: '',
            panelOpen: false,
            loading: false,
            entities: [],
            activeIndex: 0,
            searchTimer: null,
            listboxId: `admin-global-search-results-${this._uid}`,
        };
    },
    computed: {
        queryTrim() {
            return this.query.trim();
        },
        navHits() {
            const q = normalize(this.queryTrim);
            if (q.length < 2) {
                return [];
            }
            return NAV_CATALOG.filter((row) => {
                if (row.perm && !helper.hasPermission(row.perm)) {
                    return false;
                }
                if (row.path === '/configuration/locale' && !helper.getConfig('multilingual')) {
                    return false;
                }
                if (row.path === '/configuration/ip-filter' && !helper.getConfig('ip_filter')) {
                    return false;
                }
                const label = normalize(this.trans(row.i18n));
                const hay = `${label} ${row.kw}`;
                return hay.includes(q);
            }).map((row) => ({
                path: row.path,
                label: this.trans(row.i18n),
            }));
        },
        entityBlocks() {
            return this.entities.filter((b) => b.items && b.items.length);
        },
        flatCount() {
            let n = this.navHits.length;
            this.entityBlocks.forEach((b) => {
                n += b.items.length;
            });
            return n;
        },
        flatIndex() {
            if (this.flatCount === 0) {
                return -1;
            }
            return Math.min(Math.max(0, this.activeIndex), this.flatCount - 1);
        },
        activeOptionId() {
            return this.flatIndex >= 0 ? this.optionId(this.flatIndex) : '';
        },
    },
    watch: {
        query() {
            this.scheduleSearch();
            this.activeIndex = 0;
        },
        navHits() {
            this.activeIndex = 0;
        },
        entityBlocks() {
            this.activeIndex = 0;
        },
    },
    mounted() {
        this._onKey = (e) => {
            if (e.key === '/' && !e.ctrlKey && !e.metaKey && !e.altKey) {
                const t = e.target;
                const tag = t && t.tagName;
                if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT' || (t && t.isContentEditable)) {
                    return;
                }
                e.preventDefault();
                this.openPanel();
                this.$nextTick(() => {
                    if (this.$refs.input) {
                        this.$refs.input.focus();
                    }
                });
            }
        };
        window.addEventListener('keydown', this._onKey);
    },
    beforeDestroy() {
        window.removeEventListener('keydown', this._onKey);
        if (this.searchTimer) {
            clearTimeout(this.searchTimer);
        }
    },
    methods: {
        openPanel() {
            this.panelOpen = true;
        },
        closePanel() {
            this.panelOpen = false;
            this.loading = false;
        },
        onFocus() {
            this.openPanel();
            if (this.queryTrim.length >= 2) {
                this.scheduleSearch(true);
            }
        },
        onPick() {
            this.closePanel();
            this.query = '';
            this.entities = [];
        },
        scheduleSearch(immediate) {
            if (this.searchTimer) {
                clearTimeout(this.searchTimer);
            }
            if (this.queryTrim.length < 2) {
                this.entities = [];
                this.loading = false;
                return;
            }
            if (immediate) {
                this.runSearch();
                return;
            }
            this.searchTimer = setTimeout(() => this.runSearch(), 280);
        },
        runSearch() {
            const q = this.queryTrim;
            if (q.length < 2) {
                return;
            }
            this.loading = true;
            axios
                .get('/api/search/global', { params: { q, limit: 6 } })
                .then((res) => {
                    const data = res.data || {};
                    this.entities = data.entities || [];
                })
                .catch(() => {
                    this.entities = [];
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        entityTitle(type) {
            const keys = {
                user: 'user.user',
                instance: 'instance.instance',
                subscriber: 'subscriber.subscriber',
                query: 'query.query',
                lead: 'lead.lead',
                blog: 'blog.blog',
                plan: 'plan.plan',
                currency: 'currency.currency',
                subscription: 'subscription.subscription',
            };
            return this.trans(keys[type] || 'general.main');
        },
        flatEntityIndex(block, j) {
            let offset = 0;
            for (let i = 0; i < this.entityBlocks.length; i += 1) {
                const b = this.entityBlocks[i];
                if (b === block) {
                    return offset + j;
                }
                offset += b.items.length;
            }
            return offset + j;
        },
        optionId(index) {
            return `${this.listboxId}-option-${index}`;
        },
        moveActive(delta) {
            if (!this.panelOpen || this.flatCount === 0) {
                return;
            }
            let next = this.activeIndex + delta;
            if (next < 0) {
                next = this.flatCount - 1;
            } else if (next >= this.flatCount) {
                next = 0;
            }
            this.activeIndex = next;
        },
        goActive() {
            const idx = this.flatIndex;
            if (idx < 0) {
                return;
            }
            const nav = this.navHits;
            if (idx < nav.length) {
                this.$router.push(nav[idx].path);
                this.onPick();
                return;
            }
            let rem = idx - nav.length;
            for (let i = 0; i < this.entityBlocks.length; i += 1) {
                const b = this.entityBlocks[i];
                if (rem < b.items.length) {
                    this.$router.push(b.items[rem].path);
                    this.onPick();
                    return;
                }
                rem -= b.items.length;
            }
        },
    },
};
</script>
